/**
 * Per-attachment persistent caption store — IndexedDB-backed.
 *
 * Web counterpart of `kaether_for_ios/Stoat/E2EE/AttachmentCaptionStore.swift`.
 *
 * One caption record per attachmentId; strictly-later ts wins. Captions can
 * carry sensitive text (descriptions of private images, voice-memo transcripts)
 * so the store uses IndexedDB — the browser's sandboxed origin-isolated
 * storage, analogous to iOS `.completeFileProtection`.
 *
 * ## Monotonicity rule
 *
 * - `apply(payload)` rejects strictly-older AND equal-ts updates. Equal-ts is
 *   a duplicate with nothing to learn from; it does not churn `observedAt`.
 * - Empty `captionText` OVERWRITES — this is the "edit to clear" model; the
 *   user explicitly wants an empty caption, distinct from "no caption at all".
 *   (Contrast with DraftStore which deletes on empty — captions need the
 *   record to remain so the UI knows a caption was once present and then
 *   cleared.)
 * - The store keys purely on `payload.attachmentId`. There is NO
 *   `expectedAttachmentId` parameter — a hostile server-supplied payload
 *   routes to the attachmentId named INSIDE the signed plaintext, never to
 *   one the caller "thought" it was attaching to. This prevents server-side
 *   rebind attacks; the trust boundary is the surrounding E2EE envelope.
 */

import type {
  AttachmentCaptionPayload,
} from "./AttachmentCaption";
import {
  AttachmentCaptionError,
  AttachmentCaptionCodec,
  countUnicodeScalars,
  makeAttachmentCaptionPayload,
  CAPTION_SCHEMA_VERSION,
} from "./AttachmentCaption";

// ---------------------------------------------------------------------------
// Record stored in IndexedDB
// ---------------------------------------------------------------------------

export interface AttachmentCaptionRecord {
  readonly attachmentId: string;
  readonly captionText: string;
  readonly lastTs: number;      // peer-claimed authoring time (Unix seconds)
  readonly observedAt: number;  // local Unix ms when we recorded it
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "captions";
// Default DB name for the shared singleton.
const DEFAULT_DB_NAME = "kaether:attachment-captions:v1";

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // keyed by attachmentId — one record per attachment
        db.createObjectStore(STORE_NAME, { keyPath: "attachmentId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(
  db: IDBDatabase,
  attachmentId: string,
): Promise<AttachmentCaptionRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(attachmentId);
    req.onsuccess = () =>
      resolve(req.result as AttachmentCaptionRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(
  db: IDBDatabase,
  record: AttachmentCaptionRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(
  db: IDBDatabase,
  attachmentId: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(attachmentId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbClear(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// AttachmentCaptionStore
// ---------------------------------------------------------------------------

/**
 * E2EE-aware, IndexedDB-backed per-attachment caption store.
 *
 * All methods are async because every read/write goes through IndexedDB.
 * Multiple instances with the same `dbName` share the same underlying IDB
 * object store (mirrors iOS multi-instance persistence test).
 *
 * For test isolation, pass a unique `dbName` per test case.
 *
 * Security invariant: `apply(payload)` does NOT accept a separate
 * `expectedAttachmentId` parameter — the routing key comes purely from
 * `payload.attachmentId`. Callers cannot rebind a payload to a different
 * attachment at the store layer; the E2EE envelope is the trust boundary.
 */
export class AttachmentCaptionStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  /**
   * Apply an incoming (or local) caption payload.
   *
   * Returns `true` if the stored state changed.
   *
   * Monotonicity:
   *   - Strictly-older ts → dropped (returns false).
   *   - Equal ts → no-op (returns false). Equal-ts is a duplicate.
   *   - Strictly-newer ts with empty `captionText` → writes the record with
   *     empty text (the "edit to clear" model — explicit empty caption, NOT
   *     deletion of the record).
   *   - Strictly-newer ts with non-empty `captionText` → writes the record.
   *
   * Security: there is NO `expectedAttachmentId` parameter. The store keys
   * purely on `payload.attachmentId` — mirroring the iOS implementation and
   * preventing a server-supplied routing hint from rebinding a caption.
   */
  async apply(
    payload: AttachmentCaptionPayload,
    observedAt: number = Date.now(),
  ): Promise<boolean> {
    const db = await this.db();
    const existing = await idbGet(db, payload.attachmentId);

    if (existing !== undefined && payload.ts <= existing.lastTs) {
      // Equal-ts or older — no-op. First writer wins on tie.
      return false;
    }

    const record: AttachmentCaptionRecord = {
      attachmentId: payload.attachmentId,
      captionText: payload.captionText,
      lastTs: payload.ts,
      observedAt,
    };
    await idbPut(db, record);
    return true;
  }

  /**
   * Local-author convenience: validates length cap + non-empty attachmentId,
   * persists the record, and returns the payload so the caller can ship it
   * inside the signed E2EE envelope. Updates the store as a side effect.
   *
   * Throws `AttachmentCaptionError` if:
   * - `attachmentId` is empty → `{ tag: "emptyAttachmentId" }`
   * - `captionText` scalar count > 1500 → `{ tag: "captionTooLong" }`
   */
  async setLocal(
    attachmentId: string,
    captionText: string,
    ts: number = Math.floor(Date.now() / 1000),
    observedAt: number = Date.now(),
  ): Promise<AttachmentCaptionPayload> {
    if (attachmentId === "") {
      throw new AttachmentCaptionError({ tag: "emptyAttachmentId" });
    }
    const scalarCount = countUnicodeScalars(captionText);
    if (scalarCount > AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT) {
      throw new AttachmentCaptionError({
        tag: "captionTooLong",
        unicodeScalarCount: scalarCount,
      });
    }
    const payload = makeAttachmentCaptionPayload(attachmentId, captionText, ts);
    await this.apply(payload, observedAt);
    return payload;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  /**
   * Returns the stored caption record for `attachmentId`, or `undefined` if
   * no caption has ever been authored for this attachment.
   *
   * Note: a record with empty `captionText` is a valid "explicitly cleared"
   * caption — distinct from `undefined` (no caption ever authored).
   */
  async caption(
    attachmentId: string,
  ): Promise<AttachmentCaptionRecord | undefined> {
    const db = await this.db();
    return idbGet(db, attachmentId);
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  /**
   * Remove the caption record for a single attachment.
   * Mirrors iOS `AttachmentCaptionStore.clear(attachmentId:)`.
   */
  async clear(attachmentId: string): Promise<void> {
    const db = await this.db();
    await idbDelete(db, attachmentId);
  }

  /**
   * Drop every stored caption record.
   * Mirrors iOS `AttachmentCaptionStore.resetAll()`.
   */
  async resetAll(): Promise<void> {
    const db = await this.db();
    await idbClear(db);
  }
}

// ---------------------------------------------------------------------------
// Shared singleton (production use)
// ---------------------------------------------------------------------------

/**
 * Shared singleton for production code. Tests should instantiate their own
 * `AttachmentCaptionStore` with a unique `dbName` for isolation.
 */
export const sharedAttachmentCaptionStore = new AttachmentCaptionStore();

// Re-export so callers can import everything from one place.
export {
  AttachmentCaptionError,
  AttachmentCaptionCodec,
  countUnicodeScalars,
  makeAttachmentCaptionPayload,
  CAPTION_SCHEMA_VERSION,
};
export type { AttachmentCaptionPayload };
